import React, { useState, useMemo, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useTheme } from "@mui/material/styles";
import {
    CELL_SIZE,
    NON_EMPTY_CELL_SCALE
} from '../../utils/dateUtils';
import { getTextColor } from '../../../../utils/colorUtils';

/**
 * Individual day cell component for the calendar
 * 
 * @param {Object} props
 * @param {Date} props.date - Date object for this cell
 * @param {Array} props.items - Array of items scheduled for this date
 * @param {Function} props.onDayClick - Handler for clicking the day
 * @param {boolean} props.isMoveMode - Whether in move mode
 * @param {Object} props.contentTypeColors - Map of content types to colors
 * @returns {JSX.Element}
 */

const CalendarDay = ({ date, items, onDayClick, isMoveMode, contentTypeColors }) => {
    const [isHovered, setIsHovered] = useState(false);
    const theme = useTheme();
    const lightMode = theme.palette.mode === 'light';

    // Check if this date is today or in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const cellDate = new Date(date);
    cellDate.setHours(0, 0, 0, 0);
    const isToday = today.getTime() === cellDate.getTime();
    const isPastOrToday = cellDate <= today;

    const handleClick = useCallback(() => {
        // Only allow click if date is in the future
        if (!isPastOrToday) {
            onDayClick(date, items);
        }
    }, [date, items, onDayClick, isPastOrToday]);

    // Determine if the cell should show content
    const isEmpty = items.length === 0;
    const showDayNumber = !isEmpty || isMoveMode || isHovered;

    // Calculate cell size with scaling for non-empty cells
    const cellScale = !isEmpty ? NON_EMPTY_CELL_SCALE : 1;

    // Color gradient for multiple items
    const getMultipleItemsColor = useCallback((items) => {
        if (items.length === 0) return 'transparent';
        if (items.length === 1) return contentTypeColors[items[0].contentType] || '#999';

        const colors = items.map(item => contentTypeColors[item.contentType] || '#999');
        return `linear-gradient(135deg, ${colors.join(', ')})`;
    }, [contentTypeColors]);

    // Determine background color based on items and date
    const backgroundColor = useMemo(() => {
        if (isPastOrToday) return isEmpty ? '#f0f0f0' : getMultipleItemsColor(items); // Lighter grey for past/today
        if (isMoveMode) return 'rgba(144, 202, 249, 0.15)';
        if (isEmpty) return theme.palette.mode === 'light' ? '#e0e0e0' : '#b0b0b0'; // Light grey for empty cells
        return items.length > 0 ? getMultipleItemsColor(items) : 'transparent';
    }, [items, isMoveMode, isEmpty, getMultipleItemsColor, isPastOrToday, theme.palette.mode]);

    // Determine text color (using getTextColor for content items)
    const textColor = useMemo(() => {
        if (isPastOrToday && isEmpty) return '#999'; // Muted text for past/today empty cells
        if (items.length > 0 && !isMoveMode) {
            // For single item, use the content type color
            if (items.length === 1) {
                return getTextColor(contentTypeColors[items[0].contentType] || '#999');
            }
            // For gradient backgrounds, use white text as a default for multiple items
            // A more advanced solution would analyze the dominant color of the gradient
            return '#fff';
        }
        return 'inherit';
    }, [items, isMoveMode, isPastOrToday, isEmpty, contentTypeColors]);

    // Show indicators for multiple items
    const itemCount = items.length;

    return (
        <Box
            onClick={handleClick}
            onMouseEnter={() => !isPastOrToday && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            sx={{
                width: CELL_SIZE,
                height: CELL_SIZE,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '6px',
                cursor: isPastOrToday ? 'default' : 'pointer',
                position: 'relative',
                backgroundImage: backgroundColor.includes('linear-gradient') ? backgroundColor : 'none',
                backgroundColor: !backgroundColor.includes('linear-gradient') ? backgroundColor : 'transparent',
                transition: 'all 0.2s',
                transform: isEmpty && !isMoveMode ? 'scale(1)' : `scale(${cellScale})`,
                zIndex: isEmpty ? 1 : 2, // Higher z-index for non-empty cells
                opacity: isPastOrToday ? 0.7 : 1, // Reduced opacity for past/today empty cells
                border: isToday ? '2px solid #ff9800' : 'none', // Orange border for today
                ...(isPastOrToday && {
                    // Apply diagonal strikethrough pattern for past dates if they're empty
                    backgroundImage: isEmpty ?
                        'repeating-linear-gradient(45deg, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.1) 5px, rgba(0, 0, 0, 0) 5px, rgba(0, 0, 0, 0) 10px)' :
                        (backgroundColor.includes('linear-gradient') ? backgroundColor : 'none'),
                }),
                '&:hover': {
                    opacity: isPastOrToday ? 0.7 : 1,
                    transform: isPastOrToday ?
                        (isEmpty && !isMoveMode ? 'scale(1)' : `scale(${cellScale})`) :
                        (isEmpty && !isMoveMode ? 'scale(1.05)' : `scale(${cellScale + 0.1})`),
                    boxShadow: isPastOrToday ? 'none' : theme.customShadows.z1,
                    backgroundColor: isPastOrToday ?
                        (!backgroundColor.includes('linear-gradient') ? backgroundColor : 'transparent') :
                        (isMoveMode ? 'rgba(144, 202, 249, 0.3)' : (isEmpty ? (lightMode ? '#cccccc' : '#707070') : undefined)),
                    zIndex: isPastOrToday ? (isEmpty ? 1 : 2) : 3 // Only increase z-index on hover for future dates
                }
            }}
        >
            {showDayNumber && (
                <Typography
                    variant="caption"
                    sx={{
                        color: textColor,
                        fontWeight: items.length > 0 ? 'bold' : 'normal',
                        zIndex: 1
                    }}
                >
                    {date.getDate()}
                </Typography>
            )}

            {/* Move mode indicator */}
            {isMoveMode && (
                <Box sx={{
                    position: 'absolute',
                    top: '1.5px',
                    right: '1.5px',
                    color: '#2196f3',
                    fontSize: '11px'
                }}>
                    <AddIcon fontSize="inherit" />
                </Box>
            )}

            {/* Count badge for multiple items */}
            {itemCount > 1 && (
                <Box sx={{
                    position: 'absolute',
                    bottom: '1.5px',
                    right: '1.5px',
                    bgcolor: 'rgba(255,255,255,0.8)',
                    color: '#333',
                    borderRadius: '50%',
                    width: '9px',
                    height: '9px',
                    fontSize: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold'
                }}>
                    {itemCount}
                </Box>
            )}
        </Box>
    );
};

export default React.memo(CalendarDay);